import { StatusIndicator } from '@cloudscape-design/components';
import {
  AUDIT_STATUS_LABELS,
  AUDIT_TYPE_LABELS,
  AuditStatus,
} from 'src/contstant/commonLabels';
import {
  Audit,
  GetAuditSummaryResponse,
  QuestionWithResponse,
  SubQuestionResponseEntry,
} from 'src/types/helios/Audit';
import { BusinessUnit } from 'src/types/orbit/BusinessUnit';
import {
  findBusinessUnitById,
  formatDateTime,
  getAuditStatusColor,
  getAuditStatusIcon,
} from 'src/utils/generalUtils';
import { ExportFileConfig } from 'src/utils/exportFileUtils';
import { getAssigneesDisplayValue } from 'src/helper/performAuditHelpers';

export const getAuditSummaryDefinition = (
  auditSummary: GetAuditSummaryResponse,
  businessUnitList?: BusinessUnit[],
  currentAudit?: Audit,
) => {
  return [
    { label: 'Audit ID', value: auditSummary.audit.auditId },
    { label: 'Audit Name', value: auditSummary.audit.name },
    {
      label: 'Audit Status',
      value: (
        <StatusIndicator
          type={getAuditStatusIcon(currentAudit?.status || auditSummary.audit.status)}
          colorOverride={getAuditStatusColor(
            currentAudit?.status || auditSummary.audit.status,
          )}
        >
          {
            AUDIT_STATUS_LABELS[
              (currentAudit?.status ||
                auditSummary.audit.status) as keyof typeof AuditStatus
            ]
          }
        </StatusIndicator>
      ),
    },
    { label: 'Location', value: auditSummary.audit.location },
    {
      label: 'Business Unit',
      value:
        findBusinessUnitById(businessUnitList, auditSummary.audit.businessUnit)?.name ||
        auditSummary.audit.businessUnit,
    },
    {
      label: 'Division',
      value: auditSummary.audit.divisionName || '-',
    },

    {
      label: 'Template ID',
      value: auditSummary.audit.templateId,
    },
    {
      label: 'Template Name',
      value: auditSummary.audit.templateName,
    },
    {
      label: 'Audit Type',
      value:
        AUDIT_TYPE_LABELS[
          auditSummary.audit.auditType as keyof typeof AUDIT_TYPE_LABELS
        ] || auditSummary.audit.auditType,
    },

    {
      label: 'Assigned To',
      value: getAssigneesDisplayValue(auditSummary.audit),
    },

    {
      label: 'Audit Scheduled Date',
      value: formatDateTime(auditSummary.audit.scheduledDate, 'DD MMM YYYY'),
    },
    {
      label: 'Audit Start Date',
      value: auditSummary.audit.startDate
        ? formatDateTime(auditSummary.audit.startDate, 'DD MMM YYYY')
        : '-',
    },
    {
      label: 'Audit Expiry date',
      value: auditSummary.audit.expirationDate
        ? formatDateTime(auditSummary.audit.expirationDate, 'DD MMM YYYY')
        : '-',
    },
    {
      label: 'Completed By',
      // Legacy audits (pre-completedBy/assignees fields) only carry assignedTo,
      // so fall back to it — but only when the audit is actually completed.
      // Without the status gate, in-progress audits would show their assignee
      // as the "completer".
      value:
        auditSummary.audit.completedBy ||
        ((currentAudit?.status || auditSummary.audit.status) === AuditStatus.COMPLETED
          ? auditSummary.audit.assignedTo
          : undefined) ||
        '-',
    },
    {
      label: 'Completion Date',
      value: auditSummary.audit.completionDate
        ? formatDateTime(auditSummary.audit.completionDate)
        : '-',
    },
    ...(auditSummary.audit.createdBy
      ? [
          {
            label: 'Created By',
            value: auditSummary.audit.createdBy || ' - ',
          },
        ]
      : []),
    ...(auditSummary.audit.reopenedBy
      ? [
          {
            label: 'Reopened By',
            value: auditSummary.audit.reopenedBy || ' - ',
          },
        ]
      : []),
    ...(auditSummary.audit.reopenedReason
      ? [
          {
            label: 'Reopened Reason',
            value: auditSummary.audit.reopenedReason || ' - ',
          },
        ]
      : []),
    ...(auditSummary.audit.reOpenedAt
      ? [
          {
            label: 'Reopened At',
            value: formatDateTime(auditSummary.audit.reOpenedAt),
          },
        ]
      : []),
  ];
};

/**
 * Interface representing a flattened row for export
 * Each row contains audit info, optional question info, and optional sub-question info
 */
export interface AuditExportRow {
  audit: Audit;
  question?: QuestionWithResponse;
  subQuestion?: SubQuestionResponseEntry;
}

/**
 * Flattens audit data for CSV export
 * - If no questions exist, returns one row with just audit info
 * - For each question without sub-questions, returns one row with audit + question info
 * - For each question with sub-questions, returns N rows (one per sub-question)
 */
export function flattenAuditDataForExport(
  audit: Audit,
  questions: QuestionWithResponse[],
): AuditExportRow[] {
  // If no questions, return one row with just audit info
  if (!questions || questions.length === 0) {
    return [{ audit }];
  }

  const rows: AuditExportRow[] = [];

  for (const question of questions) {
    const subQuestionResponses = question.responseDetails?.subQuestionResponses;

    // If question has sub-questions, create a row for each sub-question
    if (subQuestionResponses && subQuestionResponses.length > 0) {
      for (const subQuestion of subQuestionResponses) {
        rows.push({
          audit,
          question,
          subQuestion,
        });
      }
    } else {
      // Question without sub-questions - one row with audit + question info
      rows.push({
        audit,
        question,
      });
    }
  }

  return rows;
}

/**
 * Helper function to get sub-question details from question's localized content
 * This is needed because SubQuestionResponseEntry.subQuestion may not be populated by the API
 */
function getSubQuestionDetails(row: AuditExportRow) {
  if (!row.subQuestion?.subQuestionId || !row.question) return undefined;
  return row.question.questionDetails?.localizedContent?.content?.subQuestions?.find(
    (sq) => sq.subQuestionId === row.subQuestion?.subQuestionId,
  );
}

/**
 * Helper function to calculate percentage score
 */
function calculateScorePercent(
  score: number | undefined,
  maxScore: number | undefined,
): string {
  if (score === undefined || maxScore === undefined || maxScore === 0) return '-';
  return String(Math.round((score / maxScore) * 100 * 100) / 100);
}

/**
 * Generates the audit summary URL for a given audit
 * @param auditId - The audit ID
 * @param businessUnit - The business unit ID
 */
export function getAuditSummaryLink(auditId: string, businessUnit: string): string {
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : '';
  const params = new URLSearchParams({
    auditId,
    businessUnit,
  });
  return `${baseUrl}/audit-summary?${params}`;
}

/**
 * Creates export config for audit summary CSV export
 * @param auditId - The audit ID for the filename
 * @param businessUnit - The business unit for the filename
 */
export function getAuditSummaryExportConfig(
  auditId?: string,
  businessUnit?: string,
): ExportFileConfig {
  const timestamp = new Date().getTime();

  return {
    fileName: `Audit_Summary_${auditId}_BU_${businessUnit}_${timestamp}.csv`,
    mappings: {
      // Audit Link
      'Audit Link': (row: AuditExportRow) =>
        getAuditSummaryLink(
          row.audit.auditId,
          row.audit.businessUnit || row.audit.businessUnitId || '',
        ),
      // Audit Info
      'Audit ID': (row: AuditExportRow) => row.audit.auditId || '-',
      'Audit Name': (row: AuditExportRow) => row.audit.name || '-',
      'Audit Status': (row: AuditExportRow) =>
        AUDIT_STATUS_LABELS[row.audit.status as keyof typeof AuditStatus] ||
        row.audit.status ||
        '-',
      'Location': (row: AuditExportRow) => row.audit.location || '-',
      'Business Unit': (row: AuditExportRow) => row.audit.businessUnit || '-',
      'Division': (row: AuditExportRow) => row.audit.divisionName || '-',
      'Template ID': (row: AuditExportRow) => row.audit.templateId || '-',
      'Template Name': (row: AuditExportRow) => row.audit.templateName || '-',
      'Audit Type': (row: AuditExportRow) =>
        AUDIT_TYPE_LABELS[row.audit.auditType as keyof typeof AUDIT_TYPE_LABELS] ||
        row.audit.auditType ||
        '-',
      'Assigned To': (row: AuditExportRow) => getAssigneesDisplayValue(row.audit),
      'Scheduled Date': (row: AuditExportRow) =>
        row.audit.scheduledDate
          ? formatDateTime(row.audit.scheduledDate, 'DD MMM YYYY')
          : '-',
      'Start Date': (row: AuditExportRow) =>
        row.audit.startDate ? formatDateTime(row.audit.startDate, 'DD MMM YYYY') : '-',
      'Expiry Date': (row: AuditExportRow) =>
        row.audit.expirationDate
          ? formatDateTime(row.audit.expirationDate, 'DD MMM YYYY')
          : '-',
      'Completion Date': (row: AuditExportRow) =>
        row.audit.completionDate ? formatDateTime(row.audit.completionDate) : '-',
      'Created By': (row: AuditExportRow) => row.audit.createdBy || '-',
      'Overall Score %': (row: AuditExportRow) =>
        row.audit.metrics?.scorePercent !== undefined
          ? String(row.audit.metrics.scorePercent)
          : '-',

      // Question Info
      'Question ID': (row: AuditExportRow) =>
        row.question?.questionDetails?.questionId || '-',
      'Question Title': (row: AuditExportRow) =>
        row.question?.questionDetails?.localizedContent?.content?.questionDetails
          ?.title || '-',
      'Question Text': (row: AuditExportRow) =>
        row.question?.questionDetails?.localizedContent?.content?.questionDetails
          ?.questionText || '-',
      'Pass Criteria': (row: AuditExportRow) =>
        row.question?.questionDetails?.localizedContent?.content?.questionDetails
          ?.passCriteria || '-',
      'Fail Criteria': (row: AuditExportRow) =>
        row.question?.questionDetails?.localizedContent?.content?.questionDetails
          ?.failCriteria || '-',
      'Audit Instruction': (row: AuditExportRow) =>
        row.question?.questionDetails?.localizedContent?.content?.questionDetails
          ?.auditInstruction || '-',
      'Question Response Status': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.responseStatus || '-',
      'Question Response Result': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.responseResult || '-',
      'Question Assigned Score (Points)': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.responseScore !== undefined
          ? String(row.question.responseDetails.mainQuestionResponse.responseScore)
          : '-',
      'Question Max Score (Points)': (row: AuditExportRow) =>
        row.question?.questionDetails?.auditScore !== undefined
          ? String(row.question.questionDetails.auditScore)
          : '-',
      'Question Score (%)': (row: AuditExportRow) =>
        calculateScorePercent(
          row.question?.responseDetails?.mainQuestionResponse?.responseScore,
          row.question?.questionDetails?.auditScore,
        ),
      'Question Response Comment': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.responseComment || '-',
      'Question Not Applicable Reason': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.naReason || '-',
      'Question Responded By': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.respondedBy || '-',
      'Question Responded At': (row: AuditExportRow) =>
        row.question?.responseDetails?.mainQuestionResponse?.respondedAt
          ? formatDateTime(row.question.responseDetails.mainQuestionResponse.respondedAt)
          : '-',

      // Sub-Question Info
      'Sub-Question ID': (row: AuditExportRow) => row.subQuestion?.subQuestionId || '-',
      'Sub-Question Title': (row: AuditExportRow) =>
        getSubQuestionDetails(row)?.title || '-',
      'Sub-Question Instruction': (row: AuditExportRow) =>
        getSubQuestionDetails(row)?.instruction || '-',
      'Sub-Question Response Status': (row: AuditExportRow) =>
        row.subQuestion?.response?.responseStatus || '-',
      'Sub-Question Response Result': (row: AuditExportRow) =>
        row.subQuestion?.response?.responseResult || '-',
      'Sub-Question Assigned Score (Points)': (row: AuditExportRow) =>
        row.subQuestion?.response?.responseScore !== undefined
          ? String(row.subQuestion.response.responseScore)
          : '-',
      'Sub-Question Max Score (Points)': (row: AuditExportRow) => {
        const subQDetails = getSubQuestionDetails(row);
        return subQDetails?.score !== undefined ? String(subQDetails.score) : '-';
      },
      'Sub-Question Score (%)': (row: AuditExportRow) => {
        const subQDetails = getSubQuestionDetails(row);
        return calculateScorePercent(
          row.subQuestion?.response?.responseScore,
          subQDetails?.score,
        );
      },
      'Sub-Question Response Comment': (row: AuditExportRow) =>
        row.subQuestion?.response?.responseComment || '-',
      'Sub-Question Not Applicable Reason': (row: AuditExportRow) =>
        row.subQuestion?.response?.naReason || '-',
    },
  };
}
